import { RenderWhen } from 'legacy/lut/components/shared/renderWnen/RenderWhen'
import {
  getGeoMappingCrud,
  getNestedIds,
} from 'legacy/lut/components/widgets/public/partner360/composedReport'
import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { Table } from 'legacy/lut/library/list'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { LoaderOverlay } from 'cng-common/components/loaderOverlay'
import { Translation } from 'cng-common/features/translations/components/translation'
import { Button } from 'core-components/library/button'
import { Loader } from 'core-components/library/loader'
import { Static } from 'core-components/types'
import { Crud } from 'core/types/Crud'
import { fetchPartnersByFilter } from 'partner360/components/QBR/api/fetchPartnersByFilter'
import { TRANSLATION_KEYS } from 'partner360/components/QBR/translations'
import {
  selectQBRDocumentName,
  selectQBREntryId,
  selectQbrIsLegacyDocument,
  selectQBRPartners,
  selectQBRRtm,
} from 'partner360/features/QBR'
import { IQBRPartner } from 'partner360/models/qbr/IQBRPartners'

import { createDraftQbr } from '../../../../../../../../../../../features/QBR/api/createDraftQbr'

import { FilterArea } from './components/FilterArea'
import { DEFAULT_FILTER_VALUES, PARTNERS_TABLE_COLUMNS } from './configs'
import { getPreparedPartnerIds } from './helpers/getPreparedPartnerIds'
import { IApplyFilter } from './models'

import styles from './SelectPartnersModalContent.module.scss'

interface ISelectPartnersModalContentProps {
  onSubmitData: () => void
}
export const SelectPartnersModalContent = ({ onSubmitData }: ISelectPartnersModalContentProps) => {
  const [wip, setWip] = useState(false)
  const [isLoadingForAllModalContent, setIsLoadingForAllModalContent] = useState(false)
  const [data, setData] = useState<IQBRPartner[]>([])
  const [checkedPartners, setCheckedPartners] = useState<IQBRPartner[]>([])
  const documentEntryId = useSelector(selectQBREntryId)
  const qbrRtm = useSelector(selectQBRRtm)
  const qbrPartners = useSelector(selectQBRPartners)
  const name = useSelector(selectQBRDocumentName)
  const isLegacyDocument = useSelector(selectQbrIsLegacyDocument)
  const dispatch = useAppDispatch()

  const title = useMemo(
    () =>
      qbrRtm === 'Distributor'
        ? TRANSLATION_KEYS.addPartnersModal.titleDistributors
        : TRANSLATION_KEYS.addPartnersModal.titleCommercials,
    [qbrRtm]
  )

  const buttonText = useMemo(
    () =>
      qbrRtm === 'Distributor'
        ? TRANSLATION_KEYS.addPartnersModal.partnersCountDistributors
        : TRANSLATION_KEYS.addPartnersModal.partnersCountCommercials,
    [qbrRtm]
  )

  const staticSource = useMemo(
    () =>
      new Crud<IQBRPartner>({
        fetcher: new Static(data),
        keyField: 'PartnerID',
        defaultPaging: {
          page: 0,
          pageSize: 15,
        },
      }),
    [data]
  )

  const onFilterApply = useCallback(
    async ({ pbms, geoValues, partnerIds, isSetMyPartnersFilter }: IApplyFilter) => {
      setWip(true)
      const payload = JSON.stringify({
        Pbms: pbms,
        Geo: getNestedIds({
          values: geoValues,
          crud: getGeoMappingCrud(),
        }),
        IsSetMyPartnersFilter: Number(isSetMyPartnersFilter),
        PartnerIds: getPreparedPartnerIds(partnerIds),
        RTM: qbrRtm,
      })
      setCheckedPartners([])
      const res = await fetchPartnersByFilter(payload)
      setData(res.Data)
      setWip(false)
    },
    [qbrRtm]
  )

  const onSaveChanges = useCallback(async () => {
    setIsLoadingForAllModalContent(true)
    await dispatch(
      createDraftQbr({
        entryId: documentEntryId,
        partners: [...checkedPartners, ...qbrPartners].map((p) => p.PartnerID),
        rtm: qbrRtm,
        name,
      })
    )
    setIsLoadingForAllModalContent(false)

    onSubmitData()
  }, [checkedPartners, documentEntryId, dispatch, onSubmitData, qbrRtm, qbrPartners, name])

  const onPartnerCheck = useCallback((item: IQBRPartner) => {
    setCheckedPartners((state) => {
      const found = state.find((el) => el.PartnerID === item.PartnerID)
      if (found) {
        return state.filter((el) => el.PartnerID !== found.PartnerID)
      }
      return [...state, item]
    })
  }, [])

  useEffect(() => {
    onFilterApply(DEFAULT_FILTER_VALUES)
  }, [onFilterApply])

  return (
    <div className={styles.root}>
      <LoaderOverlay isVisible={isLoadingForAllModalContent} />
      <h3>
        <Translation text={title} />
      </h3>
      <div className={styles.alert}>
        {isLegacyDocument ? (
          <Translation text={TRANSLATION_KEYS.addPartnersModal.legacyDocumentWarning} />
        ) : (
          <Translation text={TRANSLATION_KEYS.addPartnersModal.warning} />
        )}
      </div>
      <FilterArea onApply={onFilterApply} />
      <div className={styles.saveButtonWrapper}>
        <div className={styles.saveButtonWrapper_container}>
          <Translation
            text={buttonText}
            replaceParams={[{ key: '{partnersCount}', value: String(checkedPartners.length) }]}
          />
          <Button onClick={onSaveChanges} disabled={!checkedPartners.length}>
            <Translation text={TRANSLATION_KEYS.addPartnersModal.save} />
          </Button>
        </div>
      </div>

      <RenderWhen condition={wip}>
        <Loader />
      </RenderWhen>

      <RenderWhen condition={!wip}>
        <Table<IQBRPartner>
          hasTranslate
          header
          pagination
          source={staticSource}
          columns={PARTNERS_TABLE_COLUMNS}
          useRowCheckbox
          checkedValues={checkedPartners.map((el) => ({
            key: el.PartnerID,
            value: 'full' as 'full' | 'empty' | 'indeterminate',
          }))}
          onCheck={onPartnerCheck}
        />
      </RenderWhen>
    </div>
  )
}
